import { Dispatch, SetStateAction, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FocusTrap } from "focus-trap-react";
import styles from "./CartDrawer.module.css";

import { CartConfigInterface, CartData, initialCartConfig } from "../CartTypes";
import CloseIcon from "../CartIcons/CloseIcon";
import cartWatcher from "../../CartWatcherClass";
import { ATTRIBUTE_KEY_SHORTCODES, CUSTOM_HTML_POS_OPT, getPixelsForCart } from "../../Bundles/constants";
import { TStoreContext } from "../../../context/types";
import ErrorBox from "./ErrorBox";
import AnnouncementComponent, { CustomHTMLWrapper } from "./AnnouncementComponent";
import CartProducts from "./CartProducts";
import { mergeConfig } from "../../utilities";
import UpsellsComponent from "./UpsellsComponent";
import CartFooter from "./CartFooter";
import CartIcon from "../CartIcons/CartIcon";
import { ProductObj } from "../../Bundles/Types";
import RewardsProgress from "./RewardsProgress";
import useIsMobile from "../../../hooks/useIsMobile";
import useCartDrawerLayoutHook from "../../Bundles/useCartDrawerLayoutHook";
import Button from "../../Bundles/BundlesComponents/Button";
import {
    CART_CHANGE_ANALYZED,
    CART_STATE_CHANGED,
    EMIT_CART_CLOSED,
    EMIT_CART_OPENED,
    EMIT_CART_UPDATED,
    injectScopedCSS,
} from "../../constants";
import { addBundleToCart } from "../../Bundles/RenderOnStore/utilities";
import { KINNBABY_SHOP_URL } from "../../../utilities/constants";
import RewardGift from "./MetalBirdComponents/RewardGift";
import TriggeredRewards from "./TriggeredRewards";

const CartDrawer = ({ cartConfig, sft }: { cartConfig: CartConfigInterface; sft?: string }) => {
    const [cartData, setCartData] = useState<any>(null);
    const [showCartDrawer, setShowCartDrawer] = useState(false);
    const [errors, setErrors] = useState<null | { isError: boolean; msg: string }>(null);
    const [actionInProgress, setActionInProgress] = useState(false);
    const [isUpdating, setIsUpdating] = useState(false);

    useEffect(() => {
        const body: any = document.getElementsByTagName("body")[0];
        if (showCartDrawer) {
            body?.classList?.add("overflow-hidden");
        } else {
            body.classList.remove("overflow-hidden");
            body.classList.remove("cart-sidebar-show");
        }
    }, [showCartDrawer]);

    const fetchCart = useCallback(async () => {
        cartWatcher.showCompareAtPrices = cartConfig.cart_settings.show_compare_at_price;
        cartWatcher.addProductCollections =
            cartConfig?.metal_bird_config?.product_selection_type === "collections" ||
            cartConfig?.triggered_rewards?.tiers?.some((t) => t.trigger_type === "collection");
        const data = await cartWatcher.fetchCart();
        setCartData(data);
    }, []);

    useEffect(() => {
        // for test theme data
        if (!showCartDrawer) {
            const body = document.querySelector("body");
            if (body) {
                body.classList.remove("overflow-hidden");
                body.classList.remove("cart-sidebar-show");
            }
        }
    }, [showCartDrawer]);

    useEffect(() => {
        if (showCartDrawer) {
            document.dispatchEvent(
                new CustomEvent(EMIT_CART_OPENED, {
                    detail: { source: "CartDrawer" },
                }),
            );
        }
    }, [showCartDrawer]);

    useEffect(() => {
        fetchCart();
        const openCart = (state?: null) => {
            setShowCartDrawer((ps) => state ?? !ps);
        };

        // window.openMyCart = window.openCartOnAddProduct ? openCart : () => {};
        window.openMyCart = openCart;
        return () => {
            delete window?.openMyCart;
        };
    }, []);

    useEffect(() => {
        window.is_kefi_cart_open = showCartDrawer;

        document.dispatchEvent(
            new CustomEvent(CART_STATE_CHANGED, {
                detail: {
                    isOpen: showCartDrawer,
                },
            }),
        );

        return () => {
            delete window?.is_kefi_cart_open;
        };
    }, [showCartDrawer]);

    const handleFetchCart = useCallback(
        (
            e: any = {
                detail: {
                    isCartLinesUpdating: true,
                },
            },
        ) => {
            const { isCartLinesUpdating } = e.detail;
            console._log("Cart update detected!", isCartLinesUpdating);

            (async () => {
                const data = await cartWatcher.fetchCart();
                setActionInProgress(false);

                if (isCartLinesUpdating) {
                    document.dispatchEvent(
                        new CustomEvent(EMIT_CART_UPDATED, {
                            detail: { source: "CartDrawer", cartData: data },
                        }),
                    );

                    const value = window.sessionStorage.getItem("shipping_protection_removed");
                    if (value === "true") {
                        window.location = "/checkout/" as unknown as Location;
                        window.sessionStorage.removeItem("shipping_protection_removed");
                        return;
                    }
                    if (window?.kefi_skip_cart_open) {
                        window.kefi_skip_cart_open = false;
                    } else {
                        window?.openMyCart && window?.openMyCart(true);
                    }

                    setCartData(data);
                } else {
                    setCartData((prevData: any) => ({
                        ...data,
                        items: prevData.items, // Ensure items are updated
                    }));
                }
            })();

            // Perform necessary UI updates or fetch updated cart data
        },
        [],
    );

    useEffect(() => {
        // Attach event listener
        document.addEventListener(CART_CHANGE_ANALYZED, handleFetchCart);
        // Cleanup function
        return () => {
            document.removeEventListener(CART_CHANGE_ANALYZED, handleFetchCart);
        };
    }, [handleFetchCart]);

    const handleClearCart = async () => {
        const data = await cartWatcher.clearCart();
        setCartData(data.data);
        if (data.error?.message) {
            setErrors({ isError: true, msg: data.error?.message, ...data.error });
        }
    };
    const handleChangeCart = async (payload: any) => {
        const data: any = await cartWatcher.changeCart(payload);
        setIsUpdating(false);
        if (data.error?.message) {
            setErrors({ isError: true, msg: data.error?.message, ...data.error });
        }
    };

    const updateCartData = async (payload: any, key: string = "") => {
        const data: any = await cartWatcher.updateCart(payload);
        if (data.error?.message) {
            setErrors({ isError: true, msg: data.error?.message, ...data.error });
        } else if (key) {
            if (payload?.note) {
                return;
            }
            setErrors({ isError: false, msg: `${key} updated successfully` });
        }
    };

    const addToCartFromCart = async (
        _products: any[],
        properties: any,
        noRedirect: boolean = true,
        checkoutWithShipping = false,
    ) => {
        try {
            const res = await addBundleToCart(
                _products,
                properties,
                "", // form_id
                checkoutWithShipping ? true : noRedirect ? false : cartConfig?.cart_settings?.redirect_to_checkout, // redirect
                "", // bundle type
                undefined,
            );

            if (res?.status === 422) {
                // Item sold out
                setErrors({ isError: true, msg: res.message, ...res });
                return { error: true, status: 422, message: res.message };
            }

            return { success: true, data: res };
        } catch (data: any) {
            setErrors({ isError: true, msg: data.error?.message, ...data.error });
            return { error: true, message: data.error?.message, ...data.error };
        } finally {
            setIsUpdating(false);
        }
    };

    return (
        <CartDrawerPreview
            standAlone={true}
            cartData={cartData}
            loadingData={cartData == null}
            clearCart={handleClearCart}
            changeCartItem={handleChangeCart}
            updateCartData={updateCartData}
            actionInProgress={actionInProgress}
            setActionInProgress={setActionInProgress}
            appConfigContext={
                {
                    currency: window.kefi_bundles.localization.currency,
                    money_format: window.kefi_bundles.money_format,
                    sft,
                } as TStoreContext
            }
            errors={errors}
            setErrors={setErrors}
            showCartDrawer={showCartDrawer}
            setShowCartDrawer={setShowCartDrawer}
            cartConfig={
                mergeConfig(initialCartConfig, {
                    ...cartConfig,
                }) as CartConfigInterface
            }
            addToCartFromCart={addToCartFromCart}
            handleFetchCart={handleFetchCart}
            setIsUpdating={setIsUpdating}
            isUpdating={isUpdating}
        />
    );
};

type CartDrawerPreview = {
    standAlone?: boolean;
    cartData: any;
    cartConfig: CartConfigInterface;
    loadingData?: boolean;
    clearCart?: (() => Promise<any>) | (() => void);
    changeCartItem?: ((payload: any) => Promise<void>) | (() => void);
    updateCartData?: ((payload: any, key: string) => Promise<void>) | (() => void);
    appConfigContext: TStoreContext;
    actionInProgress?: boolean;
    setActionInProgress?: (value: SetStateAction<boolean>) => void;
    errors: null | { isError: boolean; msg: string };
    setErrors: Dispatch<SetStateAction<null | { isError: boolean; msg: string }>>;
    showCartDrawer?: boolean;
    setShowCartDrawer?: Dispatch<SetStateAction<boolean>>;
    addToCartFromCart?: any;
    handleFetchCart?: any;
    templateView?: boolean;
    isUpdating?: boolean;
    setIsUpdating?: Dispatch<SetStateAction<boolean>>;
};

export const CartDrawerPreview = ({
    standAlone = false,
    cartConfig,
    cartData,
    loadingData = false,
    clearCart = () => {},
    changeCartItem = () => {},
    updateCartData = () => {},
    appConfigContext,
    actionInProgress,
    setActionInProgress = () => {},
    errors,
    setErrors,
    showCartDrawer,
    setShowCartDrawer = () => {},
    addToCartFromCart = () => {},
    handleFetchCart = () => {},
    templateView = false,
    isUpdating = false,
    setIsUpdating = () => {},
}: CartDrawerPreview) => {
    const cart: CartData = cartData;
    const { cartGlobal, cartHeaderLayout, cartFeatures } = useCartDrawerLayoutHook({
        config: cartConfig,
    });
    console._log("asdfasdfasd cartData", cartData?.items);

    const [addProductToCart, setAddProductToCart] = useState(null);
    const { currency, money_format, sft } = appConfigContext;
    const [cartFeatureOrder, setCartFeatureOrder] = useState(cartConfig?.cart_features_positions || []);
    const { isMobile } = useIsMobile();

    useMemo(() => {
        setCartFeatureOrder(cartConfig?.cart_features_positions || []);
    }, [cartConfig?.cart_features_positions]);
    console._log(typeof clearCart);

    useEffect(() => {
        if (standAlone && !loadingData && cart?.items?.length === 0) {
            sessionStorage.removeItem("autoAddedGifts");
        }
    }, [standAlone, loadingData, cart?.items?.length]);

    const handleUpdateItem = (payload: any = {}) => {
        setActionInProgress(true);
        changeCartItem(payload);
    };

    const userShopname = !standAlone ? shopify.config?.shop : window.kefi_bundles?.shop;

    const customShippingProtection =
        !!userShopname &&
        KINNBABY_SHOP_URL.includes(userShopname) &&
        cartConfig?.additional_product?.status === true &&
        cartConfig?.additional_product?.additional_product_shipping_gift_checkbox &&
        cartConfig?.additional_product?.product_type === "shipping_protection";

    window.openCartOnAddProduct = !!cartConfig?.cart_settings?.open_cart_on_product_add;
    console._log("cartConfig?.announcement_design", cartConfig?.announcement_design);

    const throwCartCloseEvent = (cta = "Close Icon") => {
        document.dispatchEvent(
            new CustomEvent(EMIT_CART_CLOSED, {
                detail: { source: "CartDrawer", cta: cta || "-" },
            }),
        );
    };

    const handleAdditionalProductAdd = async ({
        state,
        additionalProduct,
        checkoutWithShipping = false,
    }: {
        state: boolean;
        additionalProduct: ProductObj;
        checkoutWithShipping?: boolean;
    }) => {
        const lineIndex = cart?.items?.findIndex(
            (pd) => !!additionalProduct?.id?.includes(pd.product_id as unknown as string),
        );

        if (state && lineIndex == -1) {
            // add product if state on
            addToCartFromCart(
                [
                    {
                        ...additionalProduct,
                        selectedVariants: [additionalProduct.variants[0]],
                    },
                ],
                {
                    [ATTRIBUTE_KEY_SHORTCODES.__additional_products]: {},
                },
                true,
                checkoutWithShipping,
            );
        } else if (!state && lineIndex >= 0) {
            // add product if state off
            handleUpdateItem({ line: lineIndex + 1, quantity: 0 });
            if (checkoutWithShipping) {
                window.sessionStorage.setItem("shipping_protection_removed", "true");
            } else {
                window.sessionStorage.removeItem("shipping_protection_removed");
            }
        }
    };

    const components = (index: number) => ({
        announcement: cartConfig?.announcement?.announcement_status ? (
            <AnnouncementComponent cartConfig={cartConfig} standAlone={standAlone} />
        ) : (
            <CustomHTMLWrapper cartConfig={cartConfig} currentPosition={CUSTOM_HTML_POS_OPT.ABOVE_ANNOUNCEMENT} />
        ),

        reward: cartConfig?.reward?.status
            ? (() => {
                  const currentMarketId = window.kefi_bundles?.localization?.market?.id;
                  const hasMarketTiers = !!(
                      currentMarketId && cartConfig?.reward?.market_tiers?.[currentMarketId]?.length > 0
                  );
                  const currentMarketTiers = hasMarketTiers
                      ? cartConfig?.reward?.market_tiers?.[currentMarketId] || []
                      : cartConfig?.reward?.tiers || [];

                  // Reward free gifts must not count towards the reward progress
                  const isRewardFreeGift = (item: any) =>
                      item.properties?.[ATTRIBUTE_KEY_SHORTCODES.__kbd]?.[ATTRIBUTE_KEY_SHORTCODES.__freeGiftRewards] !=
                      null;
                  const nonFreeGiftItems = cart?.items?.filter((item: any) => !isRewardFreeGift(item));
                  const rewardFreeGiftsPrice = (cart?.items || [])
                      .filter(isRewardFreeGift)
                      .reduce((total: number, item: any) => total + (item.final_line_price || 0), 0);

                  return (
                      <RewardsProgress
                          cart={cart}
                          cartConfig={{
                              ...cartConfig,
                              reward: {
                                  ...cartConfig.reward,
                                  tiers: [...currentMarketTiers]
                                      .map((tier) => ({
                                          ...tier,
                                          count:
                                              cartConfig.reward.reward_basis === "cart_total" && !hasMarketTiers
                                                  ? tier.count * (window.kefi_bundles.localization.rate || 1)
                                                  : tier.count,
                                      }))
                                      .sort((a: any, b: any) => a.count - b.count),
                              },
                          }}
                          currency={currency}
                          money_format={money_format}
                          maxProgressValue={Math.max(
                              ...(currentMarketTiers?.length ? currentMarketTiers : [{ count: 0 }]).map((tier: any) =>
                                  cartConfig.reward.reward_basis === "cart_total" && !hasMarketTiers
                                      ? tier.count * (window.kefi_bundles.localization.rate || 1)
                                      : tier.count,
                              ),
                          )}
                          cartPrice={
                              cartConfig.reward.reward_basis == "cart_total"
                                  ? (cart?.items_subtotal_price - rewardFreeGiftsPrice) / 100
                                  : nonFreeGiftItems?.reduce((total: any, item: any) => total + item.quantity, 0)
                          }
                          standAlone={standAlone}
                          sft={sft}
                      />
                  );
              })()
            : null,
        triggered_rewards: cartConfig?.triggered_rewards?.status ? (
            <TriggeredRewards
                cart={{
                    ...cart,
                    items: cart?.items?.filter(
                        (item) =>
                            !item.properties?.[ATTRIBUTE_KEY_SHORTCODES.__kbd]?.[
                                ATTRIBUTE_KEY_SHORTCODES.__triggerRewards
                            ],
                    ),
                }}
                cartConfig={cartConfig}
                standAlone={standAlone}
                sft={sft}
                currency={currency}
                cartFreeGiftItems={cart?.items?.filter(
                    (item) =>
                        item.properties?.[ATTRIBUTE_KEY_SHORTCODES.__kbd]?.[ATTRIBUTE_KEY_SHORTCODES.__triggerRewards],
                )}
            />
        ) : null,
        products: (
            <CartProducts
                cart={cart}
                cartConfig={cartConfig}
                handleUpdateItem={handleUpdateItem}
                currency={currency}
                money_format={money_format}
                actionInProgress={!!actionInProgress}
                standAlone={standAlone}
                clearCart={clearCart}
                featIndex={index}
                sft={sft}
            />
        ),
        upsell: cartConfig?.upsell?.upsell_status ? (
            <UpsellsComponent
                cartConfig={cartConfig}
                currency={currency}
                money_format={money_format}
                setAddProductToCart={setAddProductToCart}
                addProductToCart={addProductToCart}
                sft={sft}
                standAlone={standAlone}
                addToCartFromCart={addToCartFromCart}
                cart={cart}
            />
        ) : null,
        free_gifts: cartConfig?.metal_bird_config?.status ? (
            <RewardGift
                cart={cart}
                cartConfig={{
                    ...cartConfig,
                    metal_bird_config: {
                        ...cartConfig.metal_bird_config,
                        count:
                            cartConfig.metal_bird_config.qualifier_type == "min_quantity"
                                ? cartConfig.metal_bird_config.count
                                : cartConfig.metal_bird_config.count * (window.kefi_bundles.localization.rate || 1),
                    },
                }}
                currency={currency}
                standAlone={standAlone}
                sft={sft}
            />
        ) : null,
    });

    useEffect(() => {
        if (cartConfig?.cart_settings?.custom_css_text) {
            injectScopedCSS(cartConfig?.cart_settings?.custom_css_text, "kefi_cart_drawer");
        }
    }, [cartConfig?.cart_settings?.custom_css_text]);

    const drawerRef = useRef<HTMLDivElement>(null);

    return !loadingData && showCartDrawer ? (
        <div
            className={`${styles.cartDrawerOverlay}  ${standAlone ? showCartDrawer && styles.open && styles.hide : ""}`}
            onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                throwCartCloseEvent("Outside CartDrawer");
                setShowCartDrawer(false);
            }}
            style={{
                height: "100vh",
                maxHeight: "100vh",
                position: standAlone ? "fixed" : "static",
                ...(standAlone
                    ? { left: 0, zIndex: 10000000 }
                    : {
                          zIndex: 1,
                          width: isMobile ? "" : getPixelsForCart(cartConfig?.cart_design?.cart_width),
                          background: "transparent",
                      }),
                ...(templateView && {
                    position: "absolute",
                    width: "100%",
                    maxWidth: "94%",
                    zoom: 0.62,
                    bottom: 60,
                    right: "unset",
                }),
            }}
            data-form-id={"kefi_cart_drawer"}
        >
            <FocusTrap
                active={standAlone}
                focusTrapOptions={{
                    // the drawer has no tabbable node until the cart renders; focus the panel itself then
                    fallbackFocus: () => drawerRef.current as HTMLDivElement,
                    // let the overlay's own click handler close the drawer
                    allowOutsideClick: true,
                    escapeDeactivates: () => {
                        throwCartCloseEvent("Escape Key");
                        setShowCartDrawer(false);
                        return false;
                    },
                }}
            >
                <div
                    ref={drawerRef}
                    tabIndex={-1}
                    {...(standAlone && { role: "dialog", "aria-modal": true, "aria-label": cartConfig?.cart_title })}
                    className={`${styles.cartDrawerWrapper} ${
                        standAlone ? styles[cartConfig?.cart_settings?.cart_position] : styles.right
                    }`}
                    style={{
                        width: isMobile
                            ? "100%"
                            : window.innerWidth < +getPixelsForCart(cartConfig?.cart_design?.cart_width, "number")
                              ? window.innerWidth
                              : getPixelsForCart(cartConfig?.cart_design?.cart_width),
                        maxWidth: isMobile
                            ? "100%"
                            : window.innerWidth < +getPixelsForCart(cartConfig?.cart_design?.cart_width, "number")
                              ? window.innerWidth
                              : getPixelsForCart(cartConfig?.cart_design?.cart_width),
                        fontFamily:
                            cartConfig?.cart_design?.cart_font_family == "Custom Store Font"
                                ? "inherit"
                                : cartConfig?.cart_design?.cart_font_family,
                        ...(templateView && {
                            width: "100%",
                            maxWidth: "100%",
                            animation: "unset",
                        }),
                    }}
                    onClick={(e) => {
                        e.stopPropagation();
                    }}
                >
                    <div
                        className={`${styles.cartContainer} ${!standAlone && styles.hideScroll}`}
                        style={{
                            background: cartConfig?.cart_design?.cart_bg_color,
                            scrollbarWidth: standAlone ? "auto" : "none",
                            ...(templateView && {
                                boxShadow: "none",
                            }),
                        }}
                    >
                        <div
                            className={styles.cartHeader}
                            style={{
                                background: cartConfig?.cart_design?.header_bg_color,
                                height: cartHeaderLayout?.headerHeight,
                                minHeight: cartHeaderLayout?.headerHeight,
                                borderBottom: cartGlobal?.removeBorders ? "none" : "2px solid #e5e7eb",
                            }}
                        >
                            <div
                                className={styles.cartTitle}
                                style={{
                                    color: cartConfig?.cart_design?.header_title_color,
                                }}
                            >
                                {cartHeaderLayout?.showIcon && (
                                    <CartIcon color={cartConfig?.cart_design?.close_button_color} />
                                )}
                                <span
                                    style={{
                                        paddingLeft: cartHeaderLayout?.showTotalItems ? 0 : 3,
                                        fontSize: cartHeaderLayout?.showTotalItems ? "" : 20,
                                        position: "relative",
                                    }}
                                >
                                    {cartConfig?.cart_title}
                                    {cartHeaderLayout?.showUnderline && (
                                        <span
                                            style={{
                                                position: "absolute",
                                                bottom: 0,
                                                transform: "translateY(100%)",
                                                display: "flex",
                                                left: 4,
                                                minHeight: 2,

                                                background: cartConfig?.cart_design
                                                    ?.header_title_underline_color_gradient_status
                                                    ? `linear-gradient(${cartConfig?.cart_design?.header_title_underline_color_gradient_direction}deg, ${cartConfig?.cart_design?.header_title_underline_color} 0%, ${cartConfig?.cart_design?.header_title_underline_color_gradient_color} 100%) `
                                                    : cartConfig?.cart_design?.header_title_underline_color,
                                                width: "100%",
                                                marginInline: "auto",
                                            }}
                                        ></span>
                                    )}
                                </span>
                                {cart?.item_count > 0 && (
                                    <span
                                        className={styles.cartItemCount}
                                        style={{
                                            color: cartConfig?.cart_design?.header_quantity_color,
                                        }}
                                    >
                                        ({cart?.item_count}){" "}
                                        {cart?.item_count > 1
                                            ? cartConfig?.cart_items_text || "Items"
                                            : cartConfig?.cart_item_text || "Item"}
                                    </span>
                                )}
                            </div>
                            <span
                                className={styles.closeButton}
                                style={{
                                    color: cartConfig?.cart_design?.close_button_color,
                                }}
                                onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    throwCartCloseEvent();
                                    setShowCartDrawer && setShowCartDrawer(false);
                                }}
                            >
                                <CloseIcon
                                    size={cartHeaderLayout?.closeIconSize}
                                    color={cartConfig?.cart_design?.close_button_color}
                                />
                            </span>
                            <ErrorBox errors={errors ?? null} setErrors={setErrors} cartConfig={cartConfig} />
                        </div>

                        {standAlone &&
                            cart.items.length == 0 &&
                            cartConfig?.reward?.status &&
                            cartConfig.reward.show_reward_on_empty_cart && (
                                <div className={styles.cartFeaturesContainer}>{components(0).reward}</div>
                            )}

                        {/* Cart Middle*/}
                        {cart?.items?.length ? (
                            <>
                                <div
                                    className={styles.cartFeaturesContainer}
                                    style={{
                                        paddingBottom:
                                            cartConfig?.additional_product?.status &&
                                            !!cartConfig?.additional_product?.additional_product_addon_checkbox &&
                                            !!cartConfig?.additional_product
                                                ?.additional_product_shipping_gift_checkbox &&
                                            cartConfig?.additional_product?.additional_addon_products
                                                ? "140px" // when both active addon as well as shipping/gift
                                                : cartFeatures.paddingBottom,
                                    }}
                                >
                                    {cartFeatureOrder.map(({ key }, index) => {
                                        // @ts-expect-error hack
                                        return components(index)[key] ? components(index)[key] : null;
                                    })}
                                </div>
                                <CartFooter
                                    cart={cart}
                                    money_format={money_format}
                                    currency={currency}
                                    setShowCartDrawer={setShowCartDrawer}
                                    standAlone={!!standAlone}
                                    cartConfig={cartConfig}
                                    addProductToCart={addProductToCart}
                                    setAddProductToCart={setAddProductToCart}
                                    addToCartFromCart={addToCartFromCart}
                                    sft={sft}
                                    setErrors={setErrors}
                                    handleFetchCart={handleFetchCart}
                                    updateCartData={updateCartData}
                                    handleAdditionalProductAdd={handleAdditionalProductAdd}
                                    changeCartItem={changeCartItem}
                                    isUpdating={isUpdating}
                                    setIsUpdating={setIsUpdating}
                                    customShippingProtection={customShippingProtection}
                                />
                            </>
                        ) : (
                            <div className={styles.noCartItemFound}>
                                <div>{cartConfig?.empty_cart_text}</div>
                                <Button
                                    title={cartConfig?.continue_shop_cta_text}
                                    className={styles.checkoutButton}
                                    onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        if (standAlone) {
                                            throwCartCloseEvent("Continue Shopping CTA");
                                            setShowCartDrawer(false);
                                        }
                                    }}
                                    style={{
                                        flexGrow: 1,
                                        borderRadius: cartConfig?.cart_design?.footer_button_radius,
                                        // @ts-expect-error hack
                                        "--buttonBg": cartConfig?.cart_design?.footer_button_bg_color,
                                        "--buttonText": cartConfig?.cart_design?.footer_button_text_color,
                                        "--buttonHoverBg": cartConfig?.cart_design?.footer_button_bg_hover_color,
                                        "--buttonHoverText": cartConfig?.cart_design?.footer_button_text_hover_color,
                                        "--buttonBgBorder": cartConfig?.cart_design?.footer_button_border_color,
                                        "--buttonHoverBgBorder":
                                            cartConfig?.cart_design?.footer_button_hover_border_color,
                                        width: "auto",
                                        maxHeight: "40px",
                                        maxWidth: "70%",
                                    }}
                                />
                            </div>
                        )}
                        {/* Cart Bottom*/}
                    </div>
                </div>
            </FocusTrap>
        </div>
    ) : (
        <></>
    );
};

export default CartDrawer;
